import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { componentisticaApi } from '../../../shared/componentistica'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading'
import { cn } from '@/lib/utils'
import {
  User,
  LogOut,
  Menu,
  X,
  Home,
  FileText,
  Users,
  Building,
  Building2,
  List,
  Calculator,
  PlusCircle,
  Stethoscope,
  Inbox,
  FileSpreadsheet,
  UserCog,
  Bug,
  Undo2,
  Wrench,
  Link2,
  ShieldAlert,
  Activity,
  CircleDot,
  Mail,
  Webhook,
  ShieldCheck,
  Palette,
  BarChart3,
  HelpCircle,
  Boxes,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Doc } from '../../../convex/_generated/dataModel'
import { getRoleLabel } from '../../../convex/lib/permissions'
import { ClinicBrandLogo } from './ClinicBrandLogo'

type NavItem = {
  name: string
  href: string
  icon: LucideIcon
  show: boolean
}

type NavSection = {
  id: string
  label: string
  items: NavItem[]
}

const MOBILE_NAV_ID = 'app-mobile-navigation'
const FOCUSABLE_DRAWER_ELEMENTS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function buildSections(profile: Doc<'profiles'>, hasPermission: (k: string) => boolean, componentisticaVisible: boolean): NavSection[] {
  const principale: NavItem[] = [
    { name: 'Dashboard', href: '/', icon: Home, show: hasPermission('view_dashboard') },
    { name: 'Prescrizioni', href: '/prescriptions', icon: FileText, show: hasPermission('view_prescriptions') || hasPermission('view_external_prescriptions') },
    {
      name: 'Nuova prescrizione',
      href: '/prescriptions/new',
      icon: PlusCircle,
      show: hasPermission('create_prescription') || hasPermission('create_external_prescription'),
    },
    {
      name: 'Contabilità',
      href: '/accounting',
      icon: Calculator,
      show: hasPermission('view_accounting'),
    },
    {
      name: 'Componentistica',
      href: '/componentistica',
      icon: Boxes,
      show: hasPermission('view_componentistica') && componentisticaVisible,
    },
    {
      name: 'Listino medico',
      href: '/listino/medico',
      icon: Stethoscope,
      show: hasPermission('view_listino_medico'),
    },
    {
      name: 'File condivisi chirurgia guidata',
      href: '/shared-files',
      icon: Link2,
      show: hasPermission('view_shared_files'),
    },
    {
      name: 'Segnalazioni ISO',
      href: '/iso',
      icon: ShieldAlert,
      show: hasPermission('view_iso_reports'),
    },
    {
      name: 'Performance',
      href: '/reports/customer-health',
      icon: Activity,
      show: hasPermission('view_customer_health'),
    },
  ].filter((i) => i.show)

  const organizzazione: NavItem[] = [
    {
      name: 'Utenti',
      href: '/users',
      icon: Users,
      show: hasPermission('manage_users'),
    },
    {
      name: 'Listino',
      href: '/listino',
      icon: List,
      show: hasPermission('view_listino') || hasPermission('view_external_prescriptions') || hasPermission('manage_external_laboratories'),
    },
    {
      name: 'Aspetto clinica',
      href: '/company/appearance',
      icon: Palette,
      show: hasPermission('manage_company_branding'),
    },
  ].filter((i) => i.show)

  const amministrazione: NavItem[] = [
    {
      name: 'Società',
      href: '/organizations',
      icon: Building2,
      show: hasPermission('view_companies') || hasPermission('manage_companies'),
    },
    {
      name: 'Cliniche',
      href: '/companies',
      icon: Building,
      show: hasPermission('view_companies') || hasPermission('manage_companies'),
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChart3,
      show: hasPermission('view_analytics'),
    },
    {
      name: 'Ruoli e permessi',
      href: '/admin/role-permissions',
      icon: ShieldCheck,
      show: hasPermission('manage_role_permissions'),
    },
    {
      name: 'Richieste catalogo',
      href: '/admin/catalog-requests',
      icon: Inbox,
      show: hasPermission('approve_catalog_listino'),
    },
    {
      name: 'Code contabilità',
      href: '/admin/contabilita-code',
      icon: FileSpreadsheet,
      show: hasPermission('manage_contabilita_esclusioni'),
    },
    {
      name: 'Impersonation',
      href: '/admin/impersonate',
      icon: UserCog,
      show: hasPermission('use_impersonation'),
    },
    {
      name: 'Riparazione prescrizioni',
      href: '/admin/repair-prescription',
      icon: Wrench,
      show: hasPermission('manage_prescriptions'),
    },
    {
      name: 'Configurazione impianti',
      href: '/admin/implant-config',
      icon: CircleDot,
      show: hasPermission('manage_implant_config'),
    },
    {
      name: 'Comunicazioni e notifiche',
      href: '/admin/communications',
      icon: Mail,
      show:
        hasPermission('manage_communication_templates') ||
        hasPermission('manage_settings'),
    },
    {
      name: 'Webhook ISO e cambio prova',
      href: '/admin/iso-webhook',
      icon: Webhook,
      show: hasPermission('manage_iso_webhook'),
    },
    {
      name: 'Configurazione componentistica',
      href: '/admin/componentistica',
      icon: Boxes,
      show: hasPermission('manage_componentistica'),
    },
    {
      name: 'Debug PrimoUp',
      href: '/debug/primoup',
      icon: Bug,
      show: hasPermission('manage_primoup_integration'),
    },
  ].filter((i) => i.show)

  const sections: NavSection[] = []
  if (principale.length) sections.push({ id: 'principale', label: 'Principale', items: principale })
  if (organizzazione.length) sections.push({ id: 'org', label: 'Organizzazione', items: organizzazione })
  if (amministrazione.length) sections.push({ id: 'admin', label: 'Amministrazione', items: amministrazione })

  return sections
}

function NavLink({
  item,
  onNavigate,
  active,
  brandColor,
}: {
  item: NavItem
  onNavigate?: () => void
  active: boolean
  brandColor?: string | null
}) {
  const Icon = item.icon
  const usesCompanyBrand = active && !!brandColor
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      title={item.name}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2',
        active
          ? usesCompanyBrand
            ? 'text-gray-950'
            : 'bg-primary/10 text-gray-950'
          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
      )}
      style={usesCompanyBrand
        ? {
            backgroundColor: 'var(--company-brand-soft)',
            boxShadow: 'inset 3px 0 0 var(--company-brand-marker)',
          }
        : undefined}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
      <span className="truncate">{item.name}</span>
    </Link>
  )
}

function isRouteActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/'
  // "/listino" non deve risultare attivo su "/listino/medico" (voce separata nel menu)
  if (href === '/listino' && pathname.startsWith('/listino/medico')) return false
  return pathname === href || pathname.startsWith(`${href}/`)
}

type AppSidebarProps = {
  brandColor?: string | null
  clinicLogoUrl?: string | null
  clinicName?: string | null
}

export function AppSidebar({ brandColor, clinicLogoUrl, clinicName }: AppSidebarProps) {
  const { profile, logout, isLoading } = useAuth()
  const { hasPermission } = usePermissions()
  const { isAuthenticated: convexAuthenticated } = useConvexAuth()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null)
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null)
  const mobileNavRef = useRef<HTMLElement>(null)
  const sessionInfo = useQuery(api.profiles.getSessionInfo, convexAuthenticated ? {} : 'skip')
  const componentisticaContext = useQuery(
    componentisticaApi.getContext,
    convexAuthenticated && profile?.active && hasPermission('view_componentistica') ? {} : 'skip'
  )
  const stopImpersonation = useMutation(api.profiles.stopImpersonation)
  const showAdminImpersonationExit =
    sessionInfo?.ruolo === 'amministratore' && !!sessionInfo.impersonating_profile_id
  const [isLargeScreen, setIsLargeScreen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  )

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const apply = () => setIsLargeScreen(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (isLargeScreen || !mobileOpen) {
      document.body.style.overflow = ''
      return
    }
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen, isLargeScreen])

  useLayoutEffect(() => {
    const mobileNav = mobileNavRef.current
    if (!mobileNav) return
    mobileNav.inert = !isLargeScreen && !mobileOpen
    return () => {
      mobileNav.inert = false
    }
  })

  useEffect(() => {
    if (isLargeScreen || !mobileOpen) return

    const opener = mobileMenuButtonRef.current
    const focusFrame = window.requestAnimationFrame(() => {
      mobileCloseButtonRef.current?.focus()
    })
    const handleDrawerKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setMobileOpen(false)
        return
      }
      if (event.key !== 'Tab' || !mobileNavRef.current) return

      const focusableElements = Array.from(
        mobileNavRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_DRAWER_ELEMENTS)
      ).filter((element) => element.offsetParent !== null)
      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]
      if (!firstFocusable || !lastFocusable) return

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault()
        lastFocusable.focus()
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault()
        firstFocusable.focus()
      } else if (!mobileNavRef.current.contains(document.activeElement)) {
        event.preventDefault()
        const nextFocusable = event.shiftKey ? lastFocusable : firstFocusable
        nextFocusable.focus()
      }
    }

    document.addEventListener('keydown', handleDrawerKeyDown)
    return () => {
      window.cancelAnimationFrame(focusFrame)
      document.removeEventListener('keydown', handleDrawerKeyDown)
      opener?.focus()
    }
  }, [isLargeScreen, mobileOpen])

  if (isLoading || !profile) {
    return (
      <div className="flex w-full shrink-0 flex-col border-b border-gray-200 bg-white lg:h-full lg:w-56 lg:max-w-[14rem] lg:border-b-0 lg:border-r xl:w-60 xl:max-w-[15rem] 2xl:w-64 2xl:max-w-[16rem]">
        <div className="flex h-14 items-center gap-2 px-3 lg:h-16 lg:border-b lg:border-gray-100">
          <img
            src="/logo.svg"
            alt=""
            className="h-8 w-auto object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
          <span className="truncate text-base font-bold text-primary">PrimoFastLab</span>
          <LoadingSpinner size="sm" />
        </div>
      </div>
    )
  }

  const sections = buildSections(
    profile,
    hasPermission,
    hasPermission('manage_componentistica') || Boolean(componentisticaContext?.organizations.length)
  )
  const helpActive = isRouteActive(location.pathname, '/help')
  const helpUsesCompanyBrand = helpActive && !!brandColor

  const sidebarBody = (
    <>
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-100 px-3 lg:h-16">
        <Link to="/" className="flex min-w-0 flex-1 items-center gap-2" onClick={() => setMobileOpen(false)}>
          <img
            src="/logo.svg"
            alt=""
            className="h-7 w-auto shrink-0 object-contain lg:h-8"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
          <span className="truncate text-base font-bold text-primary lg:text-lg">PrimoFastLab</span>
        </Link>
        <Button
          ref={mobileCloseButtonRef}
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 focus-visible:ring-gray-950 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Chiudi menu"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 lg:px-2.5" aria-label="Navigazione principale">
        {sections.map((section) => (
          <div key={section.id} className="mb-4 last:mb-0">
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.href + item.name}>
                  <NavLink
                    item={item}
                    onNavigate={() => setMobileOpen(false)}
                    active={isRouteActive(location.pathname, item.href)}
                    brandColor={brandColor}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-gray-100 p-2.5">
        {showAdminImpersonationExit && (
          <div className="mb-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full text-xs text-gray-700"
              title="Visibile solo al tuo account amministratore: termina l'operazione come altro utente"
              onClick={() => {
                setMobileOpen(false)
                void stopImpersonation({})
              }}
            >
              <Undo2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Torna account admin</span>
            </Button>
          </div>
        )}
        <div className="mb-2 rounded-lg bg-gray-50 px-2.5 py-1.5">
          <p className="truncate text-sm font-medium text-gray-900">
            {profile.nome} {profile.cognome}
          </p>
          <p className="truncate text-xs text-gray-500">{getRoleLabel(profile.ruolo)}</p>
        </div>
        <div className="flex flex-col gap-0.5">
          <Link
            to="/help"
            onClick={() => setMobileOpen(false)}
            aria-current={helpActive ? 'page' : undefined}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2',
              helpActive
                ? helpUsesCompanyBrand
                  ? 'text-gray-950'
                  : 'bg-primary/10 text-gray-950'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            )}
            style={helpUsesCompanyBrand
              ? {
                  backgroundColor: 'var(--company-brand-soft)',
                  boxShadow: 'inset 3px 0 0 var(--company-brand-marker)',
                }
              : undefined}
          >
            <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">Aiuto</span>
          </Link>
          <Link
            to="/profile"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
          >
            <User className="h-4 w-4 shrink-0" />
            <span className="truncate">Profilo</span>
          </Link>
          <button
            type="button"
            onClick={() => {
              setMobileOpen(false)
              logout()
            }}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="truncate">Esci</span>
          </button>
        </div>
      </div>
    </>
  )

  return (
    <div className="flex w-full shrink-0 flex-col lg:h-full lg:min-h-0 lg:w-56 lg:max-w-[14rem] xl:w-60 xl:max-w-[15rem] 2xl:w-64 2xl:max-w-[16rem]">
      {/* Mobile: top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-gray-200 bg-white/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 lg:hidden">
        <Button
          ref={mobileMenuButtonRef}
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0 focus-visible:ring-gray-950"
          onClick={() => setMobileOpen(true)}
          aria-label="Apri menu"
          aria-expanded={mobileOpen}
          aria-controls={MOBILE_NAV_ID}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Link
          to="/"
          className="flex min-w-0 flex-1 items-center gap-2"
          aria-label="PrimoFastLab - Dashboard"
        >
          <img
            src="/logo.svg"
            alt=""
            className="h-7 w-auto shrink-0 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
          <span className="hidden truncate font-semibold text-primary sm:inline">PrimoFastLab</span>
        </Link>
        <ClinicBrandLogo
          logoUrl={clinicLogoUrl}
          companyName={clinicName}
          compact
          className="flex shrink-0 border-l border-gray-200 pl-2"
        />
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/profile" aria-label="Profilo">
            <User className="h-5 w-5" />
          </Link>
        </Button>
      </header>

      {/* Mobile overlay */}
      {!isLargeScreen && mobileOpen && (
        <button
          type="button"
          aria-label="Chiudi menu"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar: drawer su mobile, colonna fissa su desktop */}
      <aside
        ref={mobileNavRef}
        id={MOBILE_NAV_ID}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[min(18rem,100vw)] max-w-[100vw] flex-col bg-white shadow-xl transition-transform duration-200 ease-out',
          'lg:static lg:z-auto lg:h-full lg:w-full lg:max-w-full lg:shadow-none lg:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        aria-hidden={!isLargeScreen && !mobileOpen}
        role={!isLargeScreen ? 'dialog' : undefined}
        aria-modal={!isLargeScreen && mobileOpen ? true : undefined}
        aria-label={!isLargeScreen ? 'Menu di navigazione' : undefined}
      >
        <div className="flex h-full min-h-0 flex-col border-r border-gray-200 lg:border-r-0">{sidebarBody}</div>
      </aside>
    </div>
  )
}
